import React, {
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Tender } from '@app/models';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Loader } from '@app/components';
import { TenderInfo } from '@app/common';
import {
  activateTender,
  deleteTender,
  getTenderById,
  hideTender,
} from '@app/api';
import { IconBin, IconCloseEye, IconEye } from '@app/icons';
import { ModalConfirm } from '@app/modals';

interface Props {}

function PageProfileTender(props: Props) {
  const { id } = useParams<'id'>();
  const navigate = useNavigate();
  const [tender, setTender] = useState<Tender | null>(null);
  const [pending, setPending] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [tenderIsActive, setTenderIsActive] = useState<boolean>(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [isDeleted, setIsDeleted] = useState<boolean>(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false);

  const getData = useCallback(async () => {
    try {
      const tenderResponse = await getTenderById(id as string);

      setTender(tenderResponse);
      setTenderIsActive(tenderResponse?.active || false);
      setError(false);
      setPending(false);
    } catch (e) {
      setError(true);
      setPending(false);
    }
  }, [id]);

  const handleChangeActive = useCallback(
    (tenderId: string, isActive: boolean) => {
      if (id === tenderId) {
        setTenderIsActive(isActive);
        setTender((prev) => (prev ? { ...prev, active: isActive } : null));
      }
    },
    [id]
  );

  const onClickDelete = useCallback(async () => {
    try {
      if (tender?.id) {
        await deleteTender(tender.id);
        setIsDeleted(true);
      }
    } catch (e) {
      console.error('Failed to delete tender:', e);
    }
  }, [tender?.id]);

  const onClickDeleteModal = useCallback(() => {
    setDeleteModalVisible(true);
  }, []);

  const onCloseDeleteModal = useCallback(() => {
    setDeleteModalVisible(false);
  }, []);

  const onClickActivateHideTender = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isUpdatingStatus) return;

      try {
        e.preventDefault();
        e.stopPropagation();

        if (tender?.id) {
          setIsUpdatingStatus(true);
          const newActiveState = !tenderIsActive;

          if (tenderIsActive) {
            await hideTender(tender.id);
          } else {
            await activateTender(tender.id);
          }

          setTenderIsActive(newActiveState);
          setTender((prev) =>
            prev ? { ...prev, active: newActiveState } : null
          );
          handleChangeActive(tender.id, newActiveState);
        }
      } catch (e) {
        console.error('Failed to update tender status:', e);
      } finally {
        setIsUpdatingStatus(false);
      }
    },
    [tender?.id, tenderIsActive, handleChangeActive, isUpdatingStatus]
  );

  const onEditTender = useCallback(() => {
    navigate(`/profile/tender/${id}/edit`);
  }, [id, navigate]);

  const actionButtons = useMemo<ReactNode[]>(() => {
    if (!tender) {
      return [];
    }

    const returnActionButtons = [];

    returnActionButtons.push(
      <Button
        key="delete"
        text=""
        icon={IconBin}
        onClick={onClickDeleteModal}
        variant={isDeleted ? 'contained' : 'outlined'}
        disabled={isDeleted}
      />
    );

    returnActionButtons.push(
      <Button
        key="toggle-active"
        icon={tenderIsActive ? IconEye : IconCloseEye}
        color="primary"
        onClick={onClickActivateHideTender}
        disabled={isUpdatingStatus}
      />
    );

    returnActionButtons.push(
      <Button
        key="edit"
        text="Редактировать"
        onClick={onEditTender}
        disabled={isDeleted}
      />
    );

    return returnActionButtons;
  }, [
    isDeleted,
    onClickActivateHideTender,
    onClickDeleteModal,
    onEditTender,
    tender,
    tenderIsActive,
    isUpdatingStatus,
  ]);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    const handleFocus = () => {
      if (!pending && !error) {
        getData();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [getData, pending, error]);

  if (pending) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!tender || error) {
    return (
      <div>
        <p>Тендер не найден или произошла ошибка</p>
        <Button text="Назад" onClick={() => navigate(-1)} />
      </div>
    );
  }

  return (
    <>
      <TenderInfo
        tender={tender}
        viewReplies={true}
        actions={actionButtons}
        editable={false}
      />
      <ModalConfirm
        textSuccess={'Успешно удален!'}
        onAccept={onClickDelete}
        title={'Вы уверены, что хотите удалить?'}
        visible={deleteModalVisible}
        onClose={onCloseDeleteModal}
      />
    </>
  );
}

export default memo(PageProfileTender);
